import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ProgressBar from "./ProgressBar.jsx";
import Counter from "./Counter.jsx";
import { getCoursesInfoUser } from "../service/courses.servide.js";
import { deleteAccount } from "../service/auth.servide.js";
import { getLocalUser, removeUser } from "../utils/authUtils.js";
import { AuthContext } from "../context/AuthContext.jsx";
import "../style/profilePage.css";
import { useTranslatedData } from "../data/data.jsx";
import { globalT } from "../utils/globalUtils.js";
import ReactGA from "react-ga4";

export default function ProfilePage(props) {
  const navigate = useNavigate();
  const { setUserLog } = useContext(AuthContext);

  const [userCoursesData, setUserCoursesData] = useState({
    totalCourses: 0,
    failures: 0,
    successes: 0,
  });

  const exercisesData = useTranslatedData(); // esto ejecuta t() y devuelve los textos traducidos

  const [userName, setsetUserName] = useState("");
  const [progress, setProgress] = useState(0);

  // Borrado de cuenta (Apple Guideline 5.1.1(v)): confirmación de dos pasos.
  const [deleteStep, setDeleteStep] = useState(0); // 0 = cerrado, 1 y 2 = pasos
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [deletePassword, setDeletePassword] = useState(""); // re-autenticación

  const name = getLocalUser()?.name || "";

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteStep(0);
    setDeleteError("");
    setDeletePassword("");
  };

  const handleDeleteAccount = async () => {
    const email = getLocalUser()?.email;
    if (!email) {
      setDeleteError(globalT("No se pudo eliminar la cuenta. Inténtalo de nuevo más tarde."));
      return;
    }
    if (!deletePassword) {
      setDeleteError(globalT("Introduce tu contraseña para confirmar"));
      return;
    }

    setDeleting(true);
    setDeleteError("");

    try {
      await deleteAccount(email, deletePassword);

      ReactGA.event({
        category: "Usuario",
        action: "Elimina su cuenta",
      });

      removeUser();
      setUserLog(false);
      navigate("/login");
    } catch (error) {
      setDeleting(false);
      if (error?.response?.status === 401) {
        setDeleteError(globalT("Contraseña incorrecta"));
      } else {
        setDeleteError(globalT("No se pudo eliminar la cuenta. Inténtalo de nuevo más tarde."));
      }
    }
  };

  useEffect(() => {
    props.setInit(false);
    getUserCourseData();
    setsetUserName(name);

    ReactGA.event({
      category: "Usuario",
      action: "Accede a personal profile",
    });
  }, [name, props]);

  const getUserCourseData = async () => {
    const userId = getLocalUser()?.userId;

    if (!userId) {
      return;
    }

    try {
      const { data } = await getCoursesInfoUser(userId);
      setUserCoursesData(data);

      let totalExercises = 0;
      exercisesData.forEach((element) => {
        totalExercises += element.exercices.length;
      });

      setProgress((data.successes * 100) / totalExercises);
    } catch (error) {
      if (process.env.NODE_ENV === "development") {
        console.warn("No se pudieron cargar los datos de perfil", error);
      }
    }
  };

  return (
    <div className="m-profile">
      <div className="m-title">
        {globalT("Hola")}, <span id="name">{userName}</span>,
      </div>
      <div className="m-subtitle">
        {globalT("Aquí tienes un resumen de tus avances")}
      </div>
      <div className="m-awards">
        <div className="m-awards-items">
          <div className="m-award">
            <Counter
              time={500}
              num={userCoursesData.totalCourses}
              id={"counter1"}
              initCounters={props.initCounters}
              setLoaded={props.setLoaded}
            />

            <div className="m-text">{globalT("actividades")}</div>
          </div>
          <div className="m-award">
            <Counter
              time={500}
              num={userCoursesData.successes}
              id={"counter2"}
              initCounters={props.initCounters}
              setLoaded={props.setLoaded}
            />

            <div className="m-text">{globalT("aciertos")}</div>
          </div>
          <div className="m-award">
            <Counter
              time={500}
              num={userCoursesData.failures}
              id={"counter3"}
              initCounters={props.initCounters}
              setLoaded={props.setLoaded}
            />
            <div className="m-text">{globalT("errores")}</div>
          </div>
        </div>
        <div className="m-awards-progress">
          <div className="m-subtitle">
            {globalT("¡Felicidades! Has conseguido estas medallas")}:
          </div>
          <div className="m-progress">
            <img className="m-icon" src="/images/medal.svg" alt="medal" />
            <ProgressBar
              progress={progress}
              time={500}
              initCounters={props.initCounters}
              setLoaded={props.setLoaded}
            />
          </div>
        </div>
      </div>

      <div className="m-account">
        <div className="m-subtitle">{globalT("Cuenta")}</div>
        <button
          type="button"
          className="m-account-delete-btn"
          onClick={() => {
            setDeleteError("");
            setDeleteStep(1);
          }}
        >
          {globalT("Eliminar mi cuenta")}
        </button>
      </div>

      {deleteStep > 0 && (
        <div className="m-account-modal-overlay" onClick={closeDeleteModal}>
          <div
            className="m-account-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            {deleteStep === 1 && (
              <>
                <div className="m-account-modal-title">
                  {globalT("¿Eliminar tu cuenta?")}
                </div>
                <p className="m-account-modal-text">
                  {globalT(
                    "Se borrarán tu cuenta y todos tus datos (progreso y redacciones). Esta acción no se puede deshacer."
                  )}
                </p>
                <div className="m-account-modal-actions">
                  <button
                    type="button"
                    className="m-account-modal-cancel"
                    onClick={closeDeleteModal}
                  >
                    {globalT("Cancelar")}
                  </button>
                  <button
                    type="button"
                    className="m-account-modal-danger"
                    onClick={() => setDeleteStep(2)}
                  >
                    {globalT("Continuar")}
                  </button>
                </div>
              </>
            )}

            {deleteStep === 2 && (
              <>
                <div className="m-account-modal-title">
                  {globalT("Confirmación final")}
                </div>
                <p className="m-account-modal-text">
                  {globalT(
                    "Esta acción es permanente. ¿Seguro que quieres eliminar tu cuenta de forma definitiva?"
                  )}
                </p>
                <input
                  type="password"
                  className="m-account-modal-input"
                  placeholder={globalT("Introduce tu contraseña para confirmar")}
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  autoComplete="current-password"
                  disabled={deleting}
                />
                {deleteError && (
                  <div className="m-account-modal-error">{deleteError}</div>
                )}
                <div className="m-account-modal-actions">
                  <button
                    type="button"
                    className="m-account-modal-cancel"
                    onClick={closeDeleteModal}
                    disabled={deleting}
                  >
                    {globalT("Cancelar")}
                  </button>
                  <button
                    type="button"
                    className="m-account-modal-danger"
                    onClick={handleDeleteAccount}
                    disabled={deleting}
                  >
                    {deleting
                      ? globalT("Eliminando...")
                      : globalT("Eliminar definitivamente")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
